import {createEntityAdapter, createSelector, createSlice} from "@reduxjs/toolkit";
import {useSelector} from "react-redux";
import {loadCalculation} from "./load-calculation";
import updateDetails from "./update-details";
import {calculationSettingsLoaded} from "./calculation-settings-loaded";

const choicesPricesAdapter = createEntityAdapter()
export const makeId = (choiceId, groupId, paxId) => undefined === paxId ?
    choiceId + '|' + groupId :
    choiceId + '|' + groupId + '|' + paxId

const parseNumber = (value) => {
    if (null == value) {
        return null;
    }

    if ('string' === typeof value) {
        value = value.trim();
        if ('' === value) {
            return null;
        }
    }

    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

const shouldBackfillRetail = (retailRaw, base) => {
    if (null === base) {
        return false;
    }

    if (null == retailRaw || ('string' === typeof retailRaw && '' === retailRaw.trim())) {
        return true;
    }

    const retail = parseNumber(retailRaw);
    return null !== retail && 0 === retail && 0 !== base;
}

export const choicesPricesSlice = createSlice({
    name: "calculation/choices-prices",
    initialState: choicesPricesAdapter.getInitialState({choiceIds: [], columns: [], flightPrices: false}),
    reducers: {
        setChoicePriceField: {
            prepare: (field, choiceId, groupId, paxId, value) => ({
                meta: {
                    dirty: true
                },
                payload: {
                    id: makeId(choiceId, groupId, paxId),
                    [field]: value
                }
            }),
            reducer(state, action) {
                const {id, ...fields} = action.payload;

                const changes = {}
                for (const [k, v] of Object.entries(fields)) {
                    if (v === undefined) {
                        continue;
                    }

                    changes[k] = v;
                }

                const mergedChanges = {...state.entities?.[id] || {}, ...changes}

                mergedChanges.upchargeGroupBasePrice = mergedChanges.upchargeGroupBasePrice
                    ?? mergedChanges.upchargeGroupBasePriceDefault;

                // keeps only one subsidy field, later price / subsidy is deducted by one property
                if ('memberPrice' in changes) {
                    mergedChanges.subsidyDiscount = undefined
                } else if ('subsidyDiscount' in changes) {
                    mergedChanges.memberPrice = undefined
                }

                if ('retailPrice' in changes) {
                    mergedChanges.manualFactor = undefined
                } else if ('manualFactor' in changes) {
                    mergedChanges.retailPrice = undefined
                }

                const canDeriveRetail =
                    !('retailPrice' in changes) &&
                    !('manualFactor' in changes) &&
                    ('upchargeGroupBasePrice' in changes || 'upcharge' in changes);

                if (canDeriveRetail) {
                    const base = parseNumber(mergedChanges.upchargeGroupBasePrice);
                    const delta = parseNumber(mergedChanges.upcharge) ?? 0;

                    if (shouldBackfillRetail(mergedChanges.retailPrice, base)) {
                        mergedChanges.retailPrice = String(base + delta);
                    }
                }

                // includes previously modified / untouched fields
                choicesPricesAdapter.upsertOne(state, {id, ...mergedChanges})
            }
        }
    },
    extraReducers: builder => builder
        .addCase(loadCalculation, (state, {payload: {occupancyChoices = []}}) => {
            state.choiceIds = occupancyChoices.map(({id}) => id)

            if (state.flightPrices) {
                state.choiceIds = state.choiceIds.concat(
                    occupancyChoices.map(({id}) => id + "-noflights")
                )
            }
        })
        .addCase(updateDetails, (state, {payload: {columnKeys, columns = {}, table}}) => {
            state.columns = [].concat(
                ...state.choiceIds.map(choiceId => state.useOverrideForAllPax ?
                    Object.keys(columns).map(groupId => [choiceId, groupId]) :
                    columnKeys.map(({groupId, paxId}) => [choiceId, groupId, paxId])
                )
            )
            choicesPricesAdapter.upsertMany(
                state,
                state.columns.map(([choiceId, groupId, paxId]) => {
                    const hasPax = undefined !== paxId && null !== paxId;
                    const override = hasPax ?
                        table[groupId]?.[paxId]?.[choiceId]?.override :
                        table[groupId]?.[choiceId]?.override
                    const {
                        retailPrice: retailPriceDefaultRaw,
                        upchargeGroupBasePrice: upchargeGroupBasePriceDefault,
                        manualFactor: manualFactorDefault,
                        subsidyDiscount: subsidyDiscountDefault,
                        memberPrice: memberPriceDefault,
                    } = override ?? {}

                    const baseDefault = parseNumber(upchargeGroupBasePriceDefault);
                    const retailPriceDefault = shouldBackfillRetail(retailPriceDefaultRaw, baseDefault) ?
                        baseDefault :
                        retailPriceDefaultRaw;

                    return {
                        id: makeId(choiceId, groupId, paxId),
                        retailPriceDefault,
                        upchargeGroupBasePriceDefault,
                        manualFactorDefault,
                        subsidyDiscountDefault,
                        memberPriceDefault,
                    }
                })
            )
        })
        .addCase(calculationSettingsLoaded, (state, {payload: {useOverrideForAllPax, flightGroup}}) => {
            state.useOverrideForAllPax = useOverrideForAllPax
            state.flightPrices = flightGroup && "" !== flightGroup
        })
})

export const choicesPrices = choicesPricesSlice.reducer
export default choicesPrices

export const {setChoicePriceField} = choicesPricesSlice.actions
export const {
    selectById: selectChoicePriceById,
    selectAll: selectAllChoicesPrices
} = choicesPricesAdapter.getSelectors(state => state.calculation.choicesPrices)

const selectChoicePrice = (choiceId, groupId, paxId) => createSelector(
    state => selectChoicePriceById(state, makeId(choiceId, groupId, paxId)),
    choice => choice ?? {}
)
export const useChoicePrice = (choiceId, groupId, paxId) => useSelector(selectChoicePrice(choiceId, groupId, paxId))

export const selectChoicesPricesColumns = state => state.calculation.choicesPrices.columns;
